import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { Button, IconButton, Text } from '../components/ui';

const variantStyles = {
  info: {
    background: 'rgba(59,130,246,0.12)',
    border: '1px solid rgba(59,130,246,0.3)',
    iconColor: '#60a5fa',
  },
  success: {
    background: 'rgba(45,212,191,0.12)',
    border: '1px solid rgba(45,212,191,0.3)',
    iconColor: 'var(--brand)',
  },
  warning: {
    background: 'rgba(234,179,8,0.12)',
    border: '1px solid rgba(234,179,8,0.3)',
    iconColor: '#ca8a04',
  },
  error: {
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    iconColor: '#ef4444',
  },
};

const icons = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

const NotificationPanel = ({
  variant = 'info',
  title,
  message,
  actionText,
  onActionClick,
  onClose,
  className = '',
}) => {
  const [disableAction, setDisableAction] = React.useState(false);
  const style = variantStyles[variant] || variantStyles.info;
  const Icon = icons[variant] || Info;
  const role = variant === 'error' || variant === 'warning' ? 'alert' : 'status';

  const handleActionClick = () => {
    if (onActionClick) {
      onActionClick();
      setDisableAction(true);
      setTimeout(() => setDisableAction(false), 10000);
    }
  };

  return (
    <div
      role={role}
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
      className={`flex items-start gap-3 rounded-xl p-4 ${className}`.trim()}
      style={{
        background: style.background,
        border: style.border,
      }}
    >
      <Icon className='mt-0.5 h-5 w-5 flex-shrink-0' style={{ color: style.iconColor }} />

      <div className='flex-1'>
        {title && (
          <Text variant='bodySmall' className='font-semibold' style={{ color: 'var(--text-primary)' }}>
            {title}
          </Text>
        )}
        {message && (
          <Text variant='bodySmall' className='mt-1' style={{ color: 'var(--text-body)' }}>
            {message}
          </Text>
        )}

        {actionText && onActionClick && (
          <div className='mt-3 flex justify-end'>
            <Button
              variant='text'
              size='sm'
              disabled={disableAction}
              onClick={handleActionClick}
              loading={disableAction}
              style={{ color: style.iconColor }}
            >
              {actionText}
            </Button>
          </div>
        )}
      </div>

      {onClose && (
        <IconButton
          icon={X}
          variant='ghost'
          size='sm'
          onClick={onClose}
          aria-label='Close notification'
        />
      )}
    </div>
  );
};

export default NotificationPanel;
